import {makeVar} from "@apollo/client";
import {find, isEmpty, isEqual} from "lodash";

import {parseDateTimestamp} from "@atg-shared/datetime";
import {HorseCouponPushEvent} from "@atg-tillsammans/coupon-types";

import type {
    CouponPushData,
    ProcessCouponPushDataParams,
} from "./useHorseCouponPush.types";
import type HorseCoupon from "../domain/HorseCoupon.class";
import type {TeamID, HorseCoupon as HorseCouponType} from "@atg-tillsammans/types";

export const couponPushDataVar = makeVar<CouponPushData>({});

export const setHorseCouponPushData = (
    couponId: string,
    event: HorseCouponPushEvent,
    teamId: TeamID,
    gameId: string,
    coupon?: HorseCouponType,
) =>
    couponPushDataVar({
        ...couponPushDataVar(),
        [couponId]: {
            event,
            teamId,
            gameId,
            coupon,
        },
    });

export const getCouponPushData = (couponId: string) =>
    couponPushDataVar()[couponId] || null;

export const findCouponById = (coupons: HorseCoupon[], couponId: string) =>
    find(coupons, (coupon) => coupon.id === couponId);

export const resetHorseCouponPushData = () => couponPushDataVar({});

export const shouldProcessCouponPushData = (
    previousCouponPushData: CouponPushData | null,
    couponPushData: CouponPushData,
) =>
    !!previousCouponPushData &&
    !isEmpty(couponPushData) &&
    !isEqual(previousCouponPushData, couponPushData);

export const processCouponPushData = ({
    coupons,
    couponPushData,
    handleAddCoupon,
}: ProcessCouponPushDataParams) => {
    Object.keys(couponPushData).forEach((pushedCouponId) => {
        const pushData = couponPushData[pushedCouponId];
        if (!pushData) return;

        const coupon = findCouponById(coupons, pushedCouponId);

        if (coupon && pushData.event === HorseCouponPushEvent.REMOVE) {
            coupon.removeLocally();
            return;
        }

        if (
            coupon &&
            pushData.event === HorseCouponPushEvent.UPDATE &&
            pushData.coupon?.modified &&
            parseDateTimestamp(pushData.coupon.modified).isAfter(coupon.modified)
        ) {
            coupon.setData(pushData.coupon);
        }

        if (
            !coupon &&
            pushData.event === HorseCouponPushEvent.UPDATE &&
            pushData.coupon
        ) {
            handleAddCoupon(pushData.teamId.toString(), pushData.gameId, pushData.coupon);
        }
    });
};
